import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { DataTable, EmptyState, Heading, SearchInput, Text, tabularNumsClass, type DataTableColumn } from "@lendcord/concord";
import { useGetInvestorsQuery } from "@/features/creditor/api/creditor.api";
import type { Investor } from "@/features/creditor/creditor.types";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const PAGE_SIZE = 10;

interface InvestorsListProps {
  onBack: () => void;
}

export function InvestorsList({ onBack }: InvestorsListProps) {
  const intl = useIntl();
  const { data, isLoading } = useGetInvestorsQuery();
  const investors = data?.items;
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const totalCount = investors?.length ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const pageItems = investors?.slice(startIndex, startIndex + PAGE_SIZE) ?? [];
  const rangeStart = totalCount === 0 ? 0 : startIndex + 1;
  const rangeEnd = Math.min(startIndex + PAGE_SIZE, totalCount);

  const columns: DataTableColumn<Investor>[] = [
    {
      id: "investorId",
      header: intl.formatMessage({ id: "lender.creditor.columnInvestorId" }),
      cell: (investor) => investor.investorId,
      cellClassName: tabularNumsClass,
    },
    {
      id: "name",
      header: intl.formatMessage({ id: "lender.creditor.columnInvestorName" }),
      cell: (investor) => investor.name,
    },
    {
      id: "loansOutstanding",
      header: intl.formatMessage({ id: "lender.creditor.columnLoansOutstanding" }),
      cell: (investor) => currencyFormatter.format(investor.loansOutstanding),
      cellClassName: tabularNumsClass,
    },
    {
      id: "interestEarned",
      header: intl.formatMessage({ id: "lender.creditor.columnInterestEarned" }),
      cell: (investor) => currencyFormatter.format(investor.interestEarned),
      cellClassName: tabularNumsClass,
    },
    {
      id: "walletBalance",
      header: intl.formatMessage({ id: "lender.creditor.columnWalletBalance" }),
      cell: (investor) => currencyFormatter.format(investor.walletBalance),
      cellClassName: tabularNumsClass,
    },
    {
      id: "action",
      header: intl.formatMessage({ id: "lender.creditor.columnAction" }),
      cell: () => null,
    },
  ];

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          aria-label={intl.formatMessage({ id: "lender.creditor.backToOverviewLabel" })}
          className="text-xl text-text-muted hover:text-text"
        >
          ‹
        </button>
        <Heading level={2}>
          <FormattedMessage id="lender.creditor.allInvestorsHeading" />
        </Heading>
      </div>

      <div className="mb-4 max-w-sm">
        <SearchInput
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onClear={() => setQuery("")}
          placeholder={intl.formatMessage({ id: "lender.creditor.searchInvestorsPlaceholder" })}
        />
      </div>

      <DataTable
        columns={columns}
        rows={pageItems}
        rowKey={(investor) => investor.investorId}
        loading={isLoading}
        titleColumnId="name"
        emptyState={
          <EmptyState>
            <FormattedMessage id="lender.creditor.noMatchingRecordsMessage" />
          </EmptyState>
        }
      />

      <div className="mt-4 flex items-center justify-between">
        <Text size="sm" variant="muted">
          <FormattedMessage id="lender.creditor.rowsPerPageLabel" values={{ count: PAGE_SIZE }} />
        </Text>
        <div className="flex items-center gap-3">
          <Text size="sm" variant="muted">
            <FormattedMessage
              id="lender.creditor.paginationRangeLabel"
              values={{ start: rangeStart, end: rangeEnd, total: totalCount }}
            />
          </Text>
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            aria-label={intl.formatMessage({ id: "lender.shared.previousPageLabel" })}
            className="text-text-muted disabled:opacity-40"
          >
            ‹
          </button>
          <button
            type="button"
            disabled={page >= pageCount}
            onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
            aria-label={intl.formatMessage({ id: "lender.shared.nextPageLabel" })}
            className="text-text-muted disabled:opacity-40"
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
}
